import {
  getWebInstrumentations,
  initializeFaro as initializeGrafanaFaro,
  TransportItemType,
} from '@grafana/faro-react';
import {TracingInstrumentation} from '@grafana/faro-web-tracing';

import type {BeforeSendHook} from '@grafana/faro-react';

type FaroOptions = {
  appKey: string | undefined;
  appName: string;
  appVersion: string;
  collectorUrl: string | undefined;
  enabled: boolean;
  environment: string;
  tracePropagationUrls: Array<string | undefined>;
};

type FaroTraceIdentity = {
  companyId: string;
  email: string;
};

type FaroTraceSpan = {
  setAttribute(name: string, value: string): unknown;
};

const faroTraceIdentityAttributes: Record<string, string> = {};
const traceEmailAttribute = 'qii.user.email';
const businessIdentifierPath =
  /\/(assets|images|inspections|projects)\/([^/?#]+)/gi;
const businessAttributeByPath = {
  assets: 'qii.asset.id',
  images: 'qii.image.id',
  inspections: 'qii.project.idstr',
  projects: 'qii.project.idstr',
} as const;
const businessAttributeByQuery = {
  assetId: 'qii.asset.id',
  imageId: 'qii.image.id',
  inspection: 'qii.project.idstr',
  inspectionId: 'qii.project.idstr',
  projectId: 'qii.project.idstr',
} as const;

const identifierPathSegment = /^(?:\d{6,}|[a-f\d]{16,}|[a-z\d_-]{24,})$/i;
const urlInText = /https?:\/\/[^\s"'<>]+/g;
const emailInText = /\b[\w.+-]+@[\w.-]+\.[a-z]{2,}\b/gi;
const identifierInText =
  /\b(?:\d{6,}|[a-f\d]{16,}|(?=[a-z\d_-]{24,}\b)(?=[a-z\d_-]*\d)[a-z\d_-]+)\b/gi;
const secretParameter =
  /\b(access_token|code|email|id_token|id_token_hint|link|token)=([^&\s]+)/gi;

function sanitizeUrl(value: string): string {
  try {
    const isAbsolute = /^[a-z][a-z\d+.-]*:\/\//i.test(value);
    const url = new URL(value, window.location.origin);
    url.search = '';
    url.hash = '';
    url.pathname = url.pathname
      .split('/')
      .map((segment) => (identifierPathSegment.test(segment) ? ':id' : segment))
      .join('/');

    return isAbsolute ? url.toString() : url.pathname;
  } catch {
    return value;
  }
}

function sanitizeTelemetryValue(
  value: unknown,
  redactIdentifiers: boolean
): unknown {
  if (typeof value === 'string') {
    const sanitized = value
      .replace(urlInText, sanitizeUrl)
      .replace(emailInText, '[redacted-email]')
      .replace(secretParameter, '$1=[redacted]');

    return redactIdentifiers
      ? sanitized.replace(identifierInText, '[redacted-id]')
      : sanitized;
  }
  if (Array.isArray(value)) {
    return value.map((entry) =>
      sanitizeTelemetryValue(entry, redactIdentifiers)
    );
  }
  if (value && typeof value === 'object') {
    const traceEmailEntry =
      !redactIdentifiers &&
      'key' in value &&
      value.key === traceEmailAttribute;

    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [
        key,
        !redactIdentifiers &&
        (key === traceEmailAttribute || (traceEmailEntry && key === 'value'))
          ? entry
          : sanitizeTelemetryValue(entry, redactIdentifiers),
      ])
    );
  }

  return value;
}

const sanitizeFaroItem: BeforeSendHook = (item) => ({
  ...item,
  payload: sanitizeTelemetryValue(
    item.payload,
    item.type !== TransportItemType.TRACE
  ) as typeof item.payload,
  meta: {
    ...item.meta,
    page: item.meta.page
      ? {
          ...item.meta.page,
          url: item.meta.page.url ? sanitizeUrl(item.meta.page.url) : undefined,
        }
      : undefined,
  },
});

function normalizeBusinessIdentifier(value: string): string | undefined {
  try {
    const decoded = decodeURIComponent(value);
    return /^[a-z\d_-]{1,128}$/i.test(decoded) ? decoded : undefined;
  } catch {
    return undefined;
  }
}

function setBusinessIdentifierAttribute(
  span: FaroTraceSpan,
  name: string,
  value: string
): void {
  const identifier = normalizeBusinessIdentifier(value);
  if (identifier) span.setAttribute(name, identifier);
}

function applyFaroTraceAttributes(
  span: FaroTraceSpan,
  requestUrl: string
): void {
  for (const [name, value] of Object.entries(faroTraceIdentityAttributes)) {
    span.setAttribute(name, value);
  }

  try {
    const url = new URL(requestUrl, window.location.origin);

    for (const [, resource, value] of url.pathname.matchAll(
      businessIdentifierPath
    )) {
      setBusinessIdentifierAttribute(
        span,
        businessAttributeByPath[
          resource.toLowerCase() as keyof typeof businessAttributeByPath
        ],
        value
      );
    }

    for (const [parameter, name] of Object.entries(
      businessAttributeByQuery
    )) {
      const value = url.searchParams.get(parameter);
      if (value) setBusinessIdentifierAttribute(span, name, value);
    }
  } catch {
    // Identity attributes still apply when an instrumentation reports no URL.
  }
}

export function setFaroTraceIdentity({
  companyId,
  email,
}: FaroTraceIdentity): void {
  faroTraceIdentityAttributes['qii.company.id'] = companyId;

  const normalizedEmail = email.trim().toLowerCase();
  if (/^[\w.+-]+@[\w.-]+\.[a-z]{2,}$/i.test(normalizedEmail)) {
    faroTraceIdentityAttributes[traceEmailAttribute] = normalizedEmail;
  }
}

export function resetFaroTraceIdentity(): void {
  for (const name of Object.keys(faroTraceIdentityAttributes)) {
    delete faroTraceIdentityAttributes[name];
  }
}

export function initializeFaro({
  appKey,
  appName,
  appVersion,
  collectorUrl,
  enabled,
  environment,
  tracePropagationUrls,
}: FaroOptions): void {
  if (!enabled || !collectorUrl || !appKey) return;

  const tracePropagationTargets = tracePropagationUrls.flatMap((url) => {
    if (!url) return [];

    const origin = new URL(url).origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    return [new RegExp(`^${origin}(?:/|$)`)];
  });

  initializeGrafanaFaro({
    url: `${collectorUrl}/${appKey}`,
    app: {
      name: appName,
      version: appVersion,
      environment,
    },
    beforeSend: sanitizeFaroItem,
    ignoreUrls: [
      /^https:\/\/[^/]+\.b2clogin\.com\//,
      /^https:\/\/qiistaticcontent\.blob\.core\.windows\.net\//,
      /^https:\/\/[^/]+\.ingest\.sentry\.io\//,
    ],
    ignoreErrors: [
      /^ResizeObserver loop limit exceeded$/,
      /^ResizeObserver loop completed with undelivered notifications$/,
      /^Script error\.$/,
      /chrome-extension:\/\//,
      /moz-extension:\/\//,
    ],
    sessionTracking: {
      persistent: true,
    },
    instrumentations: [
      ...getWebInstrumentations({captureConsole: false}),
      new TracingInstrumentation({
        instrumentationOptions: {
          propagateTraceHeaderCorsUrls: tracePropagationTargets,
          fetchInstrumentationOptions: {
            applyCustomAttributesOnSpan: (span, request, result) => {
              const requestUrl =
                request instanceof Request
                  ? request.url
                  : result instanceof Response
                    ? result.url
                    : '';
              applyFaroTraceAttributes(span, requestUrl);
            },
          },
          xhrInstrumentationOptions: {
            applyCustomAttributesOnSpan: (span, request) => {
              applyFaroTraceAttributes(span, request.responseURL);
            },
          },
        },
      }),
    ],
  });
}
